import { CorrespondenceRequestCategoryDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CorrespondenceRequestCategoryActions from './request-category.actions';

export const CORRESPONDENCE_REQUEST_CATEGORY_FEATURE_KEY =
  'correspondenceRequestCategory';

export interface CorrespondenceRequestCategoryState
  extends EntityState<CorrespondenceRequestCategoryDto> {
  selectedCorrespondenceRequestCategoryId?: string | number; // which CorrespondenceRequestCategory record has been selected
  loaded: boolean; // has the CorrespondenceRequestCategory list been loaded
  error?: string | null; // last none error (if any)
}

export interface CorrespondenceRequestCategoryPartialState {
  readonly [CORRESPONDENCE_REQUEST_CATEGORY_FEATURE_KEY]: CorrespondenceRequestCategoryState;
}

export const correspondenceRequestCategoryAdapter: EntityAdapter<CorrespondenceRequestCategoryDto> = createEntityAdapter<
  CorrespondenceRequestCategoryDto
>({
  selectId: (correspondenceRequestCategory) => correspondenceRequestCategory.id,
  sortComparer: false,
});

export const initialState: CorrespondenceRequestCategoryState = correspondenceRequestCategoryAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedCorrespondenceRequestCategoryId: null,
  }
);

const correspondenceRequestCategoryReducer = createReducer(
  initialState,
  on(
    CorrespondenceRequestCategoryActions.loadCorrespondenceRequestCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.loadCorrespondenceRequestCategorySuccess,
    (state, { correspondenceRequestCategory }) =>
      correspondenceRequestCategoryAdapter.addAll(
        correspondenceRequestCategory,
        { ...state, loaded: false }
      )
  ),
  on(
    CorrespondenceRequestCategoryActions.loadCorrespondenceRequestCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.addCorrespondenceRequestCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.addCorrespondenceRequestCategorySuccess,
    (state, { newCorrespondenceRequestCategory }) =>
      correspondenceRequestCategoryAdapter.addMany(
        newCorrespondenceRequestCategory,
        { ...state, loaded: false }
      )
  ),
  on(
    CorrespondenceRequestCategoryActions.addCorrespondenceRequestCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.updateCorrespondenceRequestCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.updateCorrespondenceRequestCategorySuccess,
    (state, { updateCorrespondenceRequestCategory }) =>
      correspondenceRequestCategoryAdapter.upsertMany(
        updateCorrespondenceRequestCategory,
        { ...state, loaded: false }
      )
  ),
  on(
    CorrespondenceRequestCategoryActions.updateCorrespondenceRequestCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.removeCorrespondenceRequestCategory,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceRequestCategoryActions.removeCorrespondenceRequestCategorySuccess,
    (state, { ids }) =>
      correspondenceRequestCategoryAdapter.removeMany(ids, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceRequestCategoryActions.removeCorrespondenceRequestCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: CorrespondenceRequestCategoryState | undefined,
  action: Action
) {
  return correspondenceRequestCategoryReducer(state, action);
}
